import { useEffect, useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Bot, UserRound, Loader2, Search } from "lucide-react";
import { useCrmConversas, useAtualizarEstagio } from "@/hooks/useCrm";
import { ESTAGIO_COLOR, ESTAGIO_LABEL, KANBAN_COLUNAS } from "@/types/crm";
import type { CrmConversa, CrmEstagio } from "@/types/crm";
import { iniciais } from "./crmUtils";

type Props = {
  onOpenConversa: (id: string) => void;
  busca?: string;
  onBuscaChange?: (v: string) => void;
  apenasMinhas?: boolean;
  onApenasMinhasChange?: (v: boolean) => void;
  destaqueEstagio?: CrmEstagio | null;
};

export function CrmKanban({
  onOpenConversa,
  busca = "",
  onBuscaChange,
  apenasMinhas = false,
  onApenasMinhasChange,
  destaqueEstagio = null,
}: Props) {
  const { data: conversas = [], isLoading } = useCrmConversas({ busca, apenasMinhas });
  const atualizar = useAtualizarEstagio();
  const [arrastandoId, setArrastandoId] = useState<string | null>(null);
  const [colunaHover, setColunaHover] = useState<CrmEstagio | null>(null);
  const [arrastou, setArrastou] = useState(false);

  const porColuna = useMemo(() => {
    const mapa: Record<CrmEstagio, CrmConversa[]> = {
      novo: [],
      aguardando_resposta: [],
      ia_atendendo: [],
      humano_atendendo: [],
      renovacao_andamento: [],
      fechado_ganho: [],
      fechado_perdido: [],
      sem_interesse: [],
    };
    for (const c of conversas) mapa[c.estagio]?.push(c);
    return mapa;
  }, [conversas]);

  useEffect(() => {
    if (!destaqueEstagio) return;
    document
      .getElementById(`kanban-col-${destaqueEstagio}`)
      ?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [destaqueEstagio]);

  const onDrop = (estagio: CrmEstagio) => {
    if (arrastandoId) {
      const conv = conversas.find((c) => c.id === arrastandoId);
      if (conv && conv.estagio !== estagio) {
        atualizar.mutate({ conversaId: arrastandoId, estagio });
      }
    }
    setArrastandoId(null);
    setColunaHover(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-fynix-muted">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-fynix-muted" />
          <Input
            value={busca}
            onChange={(e) => onBuscaChange?.(e.target.value)}
            placeholder="Filtrar cards"
            className="pl-9 h-10 rounded-full"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-fynix-ink-soft">
          <input
            type="checkbox"
            checked={apenasMinhas}
            onChange={(e) => onApenasMinhasChange?.(e.target.checked)}
            className="rounded border-fynix-line"
          />
          Minhas conversas
        </label>
      </div>

      <div className="overflow-x-auto pb-4">
        <div className="flex gap-3 min-w-max">
          {KANBAN_COLUNAS.map((estagio) => {
            const cards = porColuna[estagio];
            const destaque = destaqueEstagio === estagio;
            return (
              <div
                key={estagio}
                id={`kanban-col-${estagio}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setColunaHover(estagio);
                }}
                onDragLeave={() => setColunaHover((prev) => (prev === estagio ? null : prev))}
                onDrop={() => onDrop(estagio)}
                className={`w-72 shrink-0 rounded-3xl border bg-white overflow-hidden ${
                  destaque ? "border-fynix-brand" : "border-fynix-line"
                } ${colunaHover === estagio ? "ring-2 ring-fynix-brand ring-offset-2" : ""}`}
              >
                <div className="px-3 py-3 border-b border-fynix-line flex items-center justify-between bg-fynix-surface-muted">
                  <span className="text-sm font-semibold text-fynix-ink">{ESTAGIO_LABEL[estagio]}</span>
                  <Badge variant="secondary" className="text-[10px] border-0 bg-white text-fynix-ink-soft">
                    {cards.length}
                  </Badge>
                </div>
                <div className="p-2 space-y-2 min-h-[140px] bg-fynix-surface-muted/50">
                  {cards.map((c) => (
                    <div
                      key={c.id}
                      draggable
                      onDragStart={() => {
                        setArrastou(true);
                        setArrastandoId(c.id);
                      }}
                      onDragEnd={() => setArrastandoId(null)}
                      onClick={() => {
                        if (arrastou) {
                          setArrastou(false);
                          return;
                        }
                        onOpenConversa(c.id);
                      }}
                      className={`p-3 rounded-2xl border border-fynix-line bg-white cursor-grab active:cursor-grabbing shadow-sm hover:shadow-md transition-shadow ${
                        arrastandoId === c.id ? "opacity-50" : ""
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className="h-7 w-7 rounded-full bg-fynix-brand-soft text-fynix-brand-dark flex items-center justify-center text-[10px] font-semibold shrink-0">
                          {iniciais(c.nome_contato, c.telefone_cliente)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-fynix-ink truncate">
                            {c.nome_contato || c.telefone_cliente}
                          </div>
                          <div className="text-xs text-fynix-muted truncate">{c.telefone_cliente}</div>
                        </div>
                        {c.ia_ativa ? (
                          <Bot className="h-3.5 w-3.5 text-fynix-brand-dark shrink-0" />
                        ) : (
                          <UserRound className="h-3.5 w-3.5 text-fynix-charcoal shrink-0" />
                        )}
                      </div>
                      {c.ultima_msg_preview && (
                        <p className="mt-2 text-xs text-fynix-muted line-clamp-2">{c.ultima_msg_preview}</p>
                      )}
                      <span className={`mt-2 inline-flex text-[10px] px-1.5 py-0.5 rounded-full border ${ESTAGIO_COLOR[c.estagio]}`}>
                        {ESTAGIO_LABEL[c.estagio]}
                      </span>
                    </div>
                  ))}
                  {cards.length === 0 && (
                    <div className="text-center text-xs py-8 text-fynix-muted font-medium">Nenhum card nesta coluna</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
